import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../axios/api.js";

const asMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

export const fetchVendorList = createAsyncThunk(
  "vendorMaterialSpecs/fetchVendorList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/vendors");
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch vendors"));
    }
  }
);

export const fetchVendorsWithCounts = createAsyncThunk(
  "vendorMaterialSpecs/fetchVendorsWithCounts",
  async ({ search = "", page = 0, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/vendors-with-counts", {
        params: { search, page, limit },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch vendors with counts"));
    }
  }
);

export const updateVendorInfo = createAsyncThunk(
  "vendorMaterialSpecs/updateVendorInfo",
  async ({ vendor_id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/vendor-specs/vendor/${vendor_id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to update vendor"));
    }
  }
);

export const softDeleteVendor = createAsyncThunk(
  "vendorMaterialSpecs/softDeleteVendor",
  async (vendorId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendor-specs/vendor/${vendorId}`);
      return vendorId;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to delete vendor"));
    }
  }
);

export const fetchProductList = createAsyncThunk(
  "vendorMaterialSpecs/fetchProductList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/products");
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch products"));
    }
  }
);

// What each product needs, from product_master: { Pipe: ["category","material","grade"], Others: [] ... } — an empty list is a simple product.
export const fetchProductFields = createAsyncThunk(
  "vendorMaterialSpecs/fetchProductFields",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/product-fields");
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch product fields"));
    }
  }
);

export const fetchCategoryList = createAsyncThunk(
  "vendorMaterialSpecs/fetchCategoryList",
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/categories", { params: { product } });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch categories"));
    }
  }
);

export const fetchMaterialList = createAsyncThunk(
  "vendorMaterialSpecs/fetchMaterialList",
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/materials", { params: { product } });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch materials"));
    }
  }
);

export const fetchGradeList = createAsyncThunk(
  "vendorMaterialSpecs/fetchGradeList",
  async ({ product, category, material }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/grades", {
        params: { product, category, material },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch grades"));
    }
  }
);

export const fetchSizeList = createAsyncThunk(
  "vendorMaterialSpecs/fetchSizeList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/sizes");
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch sizes"));
    }
  }
);

export const fetchScheduleList = createAsyncThunk(
  "vendorMaterialSpecs/fetchScheduleList",
  async (size, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/schedules", { params: { size } });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch schedules"));
    }
  }
);

export const previewBulkCombinations = createAsyncThunk(
  "vendorMaterialSpecs/previewBulkCombinations",
  async ({ product, categories, materials }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/bulk-preview", {
        product,
        categories,
        materials,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to preview combinations"));
    }
  }
);

export const bulkLinkVendorSpecs = createAsyncThunk(
  "vendorMaterialSpecs/bulkLinkVendorSpecs",
  async ({ vendor_id, product, categories, materials }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/bulk-link", {
        vendor_id,
        product,
        categories,
        materials,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to bulk-link vendor specs"));
    }
  }
);

export const linkVendorSpec = createAsyncThunk(
  "vendorMaterialSpecs/linkVendorSpec",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/link", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to link vendor spec"));
    }
  }
);

export const fetchVendorSpecs = createAsyncThunk(
  "vendorMaterialSpecs/fetchVendorSpecs",
  async ({ vendorId, search = "", page = 0, limit = 25 }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/vendor-specs/${vendorId}`, {
        params: { search, page, limit },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch vendor specs"));
    }
  }
);

export const unlinkVendorSpec = createAsyncThunk(
  "vendorMaterialSpecs/unlinkVendorSpec",
  async (id, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendor-specs/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to unlink vendor spec"));
    }
  }
);

// Contact-level spec tagging — ties a specific contact to exact grades,
// restricted (server-side) to grades the vendor already carries.
export const fetchContactSpecs = createAsyncThunk(
  "vendorMaterialSpecs/fetchContactSpecs",
  async ({ contactId, search = "", page = 0, limit = 25 }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/vendor-specs/contact/${contactId}`, {
        params: { search, page, limit },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch contact specs"));
    }
  }
);

export const previewBulkContactCombinations = createAsyncThunk(
  "vendorMaterialSpecs/previewBulkContactCombinations",
  async ({ vendor_id, product, categories, materials }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/contact-bulk-preview", {
        vendor_id,
        product,
        categories,
        materials,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to preview contact combinations"));
    }
  }
);

export const bulkLinkContactSpecs = createAsyncThunk(
  "vendorMaterialSpecs/bulkLinkContactSpecs",
  async ({ vendor_id, contact_id, product, categories, materials }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/contact-bulk-link", {
        vendor_id,
        contact_id,
        product,
        categories,
        materials,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to bulk-link contact specs"));
    }
  }
);

export const unlinkContactSpec = createAsyncThunk(
  "vendorMaterialSpecs/unlinkContactSpec",
  async (id, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendor-specs/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to unlink contact spec"));
    }
  }
);

export const findMatchingVendors = createAsyncThunk(
  "vendorMaterialSpecs/findMatchingVendors",
  async ({ product, category, material, grade }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/match", {
        params: { product, category, material, grade },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to find matching vendors"));
    }
  }
);

// Accepts PDF, image, Excel, or CSV/text — the backend's file-type
// detection decides how to read it (see aiInquiryExtractionService.js).
export const extractInquiryRowsFromFile = createAsyncThunk(
  "vendorMaterialSpecs/extractInquiryRowsFromFile",
  async ({ file, rfqNo = null }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Tags the request with the RFQ it's for (if one's open) purely so it
      // shows up in api_logs — never read back from here, saving still goes
      // through the existing rfq_no-on-save flow untouched.
      if (rfqNo) formData.append("rfq_no", rfqNo);
      const response = await axiosClient.post("/api/ai-inquiry/extract-inquiry-rows", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.rows;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to extract rows from the uploaded file"));
    }
  }
);

// Calls the existing OLD system's weight-calculation endpoint directly —
// it's pure computation (no DB writes, no side effects), so safe to reuse
// as-is rather than reimplementing the formulas. Only Pipe and Tube are
// actually handled server-side; other products won't get a response.
export const calculateWeight = createAsyncThunk(
  "vendorMaterialSpecs/calculateWeight",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/material-code/material_code_weight", params);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to calculate weight"));
    }
  }
);

export const searchLeads = createAsyncThunk(
  "vendorMaterialSpecs/searchLeads",
  async (search = "", { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/meta/leads", { params: { search } });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to search leads"));
    }
  }
);

// Writes Source / Company Type / Origin onto the linked lead's solitaire
// row — the first real write to solitaire (everything else is read-only).
// Feeds the old system's vendor-origin filter, which the real Submit
// pipeline already calls.
export const updateLeadClassification = createAsyncThunk(
  "vendorMaterialSpecs/updateLeadClassification",
  async ({ rfq_no, source, vendorType, origin }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/vendor-specs/meta/leads/${rfq_no}`, { source, vendorType, origin });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to update lead classification"));
    }
  }
);

export const saveInquiryRow = createAsyncThunk(
  "vendorMaterialSpecs/saveInquiryRow",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/inquiry-rows", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to save inquiry row"));
    }
  }
);

// Updates a previously-saved row in place — lets a saved row be edited
// (pulled back into the draft table) instead of only delete-and-re-add.
export const updateInquiryRow = createAsyncThunk(
  "vendorMaterialSpecs/updateInquiryRow",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/vendor-specs/inquiry-rows/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to update inquiry row"));
    }
  }
);

// Bulk version — saves every changed row in ONE request instead of one
// request per row (Save All uses this). Save-side only; never touches
// submit/email. Each row still needs its own `id`; the backend saves each
// individually but the frontend only makes a single round trip.
export const updateInquiryRowsBulk = createAsyncThunk(
  "vendorMaterialSpecs/updateInquiryRowsBulk",
  async (rows, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/vendor-specs/inquiry-rows/bulk`, { rows });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to save inquiry rows"));
    }
  }
);

// Saves several draft rows (one RFQ's worth of line items) in one call —
// used by the multi-row entry table. Never sends anything, same as saveInquiryRow.
export const saveInquiryRowsBulk = createAsyncThunk(
  "vendorMaterialSpecs/saveInquiryRowsBulk",
  async (rows, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/inquiry-rows/bulk", { rows });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to save inquiry rows"));
    }
  }
);

// Re-checks already-saved rows against the real database — covers rows
// saved before AI-extraction validation existed, or saved without ever
// going through the AI import, which otherwise never get a red-flag check
// at all. Returns validation keyed by row id, not stored on the row itself.
export const validateInquiryRowsBulk = createAsyncThunk(
  "vendorMaterialSpecs/validateInquiryRowsBulk",
  async (rows, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/inquiry-rows/validate-bulk", { rows });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to validate inquiry rows"));
    }
  }
);

// Submits several already-saved rows at once — loops the same real
// dispatch (bridge + old email pipeline, or plain fallback) per row on the
// backend, just batched so a whole page of rows can be sent together
// instead of clicking Submit on each one individually.
export const submitInquiryRowsBulk = createAsyncThunk(
  "vendorMaterialSpecs/submitInquiryRowsBulk",
  async (ids, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/vendor-specs/inquiry-rows/bulk-submit", { ids });
      return response.data;
    } catch (error) {
      // meta.problems = which rows blocked the submit (backend sends nothing in that case)
      return rejectWithValue(asMessage(error, "Failed to submit inquiry rows"), { problems: error?.response?.data?.problems || null });
    }
  }
);

export const fetchInquiryRows = createAsyncThunk(
  "vendorMaterialSpecs/fetchInquiryRows",
  async ({ search = "", page = 0, limit = 25 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/inquiry-rows", {
        params: { search, page, limit },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch inquiry rows"));
    }
  }
);

// Every row id matching a search, with no pagination — used by "Submit All"
// so it actually submits every matching row, not just the current page.
export const fetchAllInquiryRowIds = createAsyncThunk(
  "vendorMaterialSpecs/fetchAllInquiryRowIds",
  async (search = "", { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/inquiry-rows/all-ids", {
        params: { search },
      });
      return response.data.ids;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch all row ids"));
    }
  }
);

// Every not-yet-submitted row, on every page, that Submit would refuse — with the reason. Read-only: sends nothing.
// The rows replace `inquiryRows` (the table shows only these); `inquiryRowsTotal` is left alone.
export const fetchInquiryRowProblems = createAsyncThunk(
  "vendorMaterialSpecs/fetchInquiryRowProblems",
  async (search = "", { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/vendor-specs/inquiry-rows/problems", {
        params: { search },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to check the inquiry rows"));
    }
  }
);

export const notifyMatchedVendors = createAsyncThunk(
  "vendorMaterialSpecs/notifyMatchedVendors",
  async (rowId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/api/vendor-specs/inquiry-rows/${rowId}/notify`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to notify vendors"));
    }
  }
);

// Real record of who actually got emailed for this row, pulled from
// email_logs (the same table the old system's send pipeline writes to on
// every successful send) — read-only, doesn't send anything.
export const fetchSentEmailLogs = createAsyncThunk(
  "vendorMaterialSpecs/fetchSentEmailLogs",
  async (rowId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/vendor-specs/inquiry-rows/${rowId}/email-logs`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch sent email logs"));
    }
  }
);

// Same as fetchSentEmailLogs, but looked up directly by RFQ number instead
// of one saved row's id — used for a page-level "who got emailed" view.
export const fetchSentEmailLogsByRfq = createAsyncThunk(
  "vendorMaterialSpecs/fetchSentEmailLogsByRfq",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/vendor-specs/rfq/${encodeURIComponent(rfq_no)}/email-logs`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to fetch sent email logs"));
    }
  }
);

// Submits a saved (draft) inquiry row — the ONLY action that actually
// dispatches the real email. Save never sends anything.
export const submitInquiryRow = createAsyncThunk(
  "vendorMaterialSpecs/submitInquiryRow",
  async (rowId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/api/vendor-specs/inquiry-rows/${rowId}/submit`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to submit inquiry row"));
    }
  }
);

export const deleteInquiryRow = createAsyncThunk(
  "vendorMaterialSpecs/deleteInquiryRow",
  async (id, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendor-specs/inquiry-rows/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(asMessage(error, "Failed to delete inquiry row"));
    }
  }
);

const vendorMaterialSpecsSlice = createSlice({
  name: "vendorMaterialSpecs",
  initialState: {
    vendors: [],
    vendorsWithCounts: [],
    vendorsTotal: 0,
    products: [],
    productFields: {},
    categories: [],
    materials: [],
    grades: [],
    sizes: [],
    schedules: [],
    specs: [],
    specsTotal: 0,
    matchedVendors: [],
    previewCombinations: [],
    previewCount: 0,
    contactSpecs: [],
    contactSpecsTotal: 0,
    previewContactCombinations: [],
    previewContactCount: 0,
    contactSpecsLoading: false,
    contactPreviewing: false,
    contactBulkLinking: false,
    inquiryRows: [],
    inquiryRowsTotal: 0,
    lastSavedRow: null,
    notifyResult: null,
    extractedRows: [],
    extracting: false,
    leadOptions: [],
    leadSearching: false,
    previewing: false,
    bulkLinking: false,
    loading: false,
    linking: false,
    matching: false,
    saving: false,
    notifying: false,
    error: null,
  },
  reducers: {
    clearMatchResults: (state) => {
      state.matchedVendors = [];
    },
    clearPreview: (state) => {
      state.previewCombinations = [];
      state.previewCount = 0;
    },
    clearExtractedRows: (state) => {
      state.extractedRows = [];
    },
    removeExtractedRow: (state, action) => {
      state.extractedRows.splice(action.payload, 1);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendorList.fulfilled, (state, action) => {
        state.vendors = action.payload;
      })
      .addCase(fetchVendorsWithCounts.fulfilled, (state, action) => {
        state.vendorsWithCounts = action.payload.rows;
        state.vendorsTotal = action.payload.total;
      })
      .addCase(updateVendorInfo.fulfilled, (state) => {
        // caller re-fetches the list; nothing to patch locally here
      })
      .addCase(softDeleteVendor.fulfilled, (state, action) => {
        state.vendorsWithCounts = state.vendorsWithCounts.filter((v) => v.vendor_id !== action.payload);
      })
      .addCase(fetchProductList.fulfilled, (state, action) => {
        state.products = action.payload;
      })
      .addCase(fetchProductFields.fulfilled, (state, action) => {
        state.productFields = action.payload;
      })
      .addCase(fetchCategoryList.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchMaterialList.fulfilled, (state, action) => {
        state.materials = action.payload;
      })
      .addCase(fetchGradeList.fulfilled, (state, action) => {
        state.grades = action.payload;
      })
      .addCase(fetchSizeList.fulfilled, (state, action) => {
        state.sizes = action.payload;
      })
      .addCase(fetchScheduleList.fulfilled, (state, action) => {
        state.schedules = action.payload;
      })
      .addCase(fetchVendorSpecs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorSpecs.fulfilled, (state, action) => {
        state.loading = false;
        state.specs = action.payload.rows;
        state.specsTotal = action.payload.total;
      })
      .addCase(fetchVendorSpecs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(linkVendorSpec.pending, (state) => {
        state.linking = true;
        state.error = null;
      })
      .addCase(linkVendorSpec.fulfilled, (state) => {
        state.linking = false;
      })
      .addCase(linkVendorSpec.rejected, (state, action) => {
        state.linking = false;
        state.error = action.payload;
      })
      .addCase(unlinkVendorSpec.fulfilled, (state, action) => {
        state.specs = state.specs.filter((s) => s.id !== action.payload);
      })
      .addCase(findMatchingVendors.pending, (state) => {
        state.matching = true;
        state.error = null;
      })
      .addCase(findMatchingVendors.fulfilled, (state, action) => {
        state.matching = false;
        state.matchedVendors = action.payload;
      })
      .addCase(findMatchingVendors.rejected, (state, action) => {
        state.matching = false;
        state.error = action.payload;
      })
      .addCase(previewBulkCombinations.pending, (state) => {
        state.previewing = true;
        state.error = null;
      })
      .addCase(previewBulkCombinations.fulfilled, (state, action) => {
        state.previewing = false;
        state.previewCombinations = action.payload.combinations;
        state.previewCount = action.payload.count;
      })
      .addCase(previewBulkCombinations.rejected, (state, action) => {
        state.previewing = false;
        state.error = action.payload;
      })
      .addCase(bulkLinkVendorSpecs.pending, (state) => {
        state.bulkLinking = true;
        state.error = null;
      })
      .addCase(bulkLinkVendorSpecs.fulfilled, (state) => {
        state.bulkLinking = false;
        state.previewCombinations = [];
        state.previewCount = 0;
      })
      .addCase(bulkLinkVendorSpecs.rejected, (state, action) => {
        state.bulkLinking = false;
        state.error = action.payload;
      })
      .addCase(fetchContactSpecs.pending, (state) => {
        state.contactSpecsLoading = true;
        state.error = null;
      })
      .addCase(fetchContactSpecs.fulfilled, (state, action) => {
        state.contactSpecsLoading = false;
        state.contactSpecs = action.payload.rows;
        state.contactSpecsTotal = action.payload.total;
      })
      .addCase(fetchContactSpecs.rejected, (state, action) => {
        state.contactSpecsLoading = false;
        state.error = action.payload;
      })
      .addCase(previewBulkContactCombinations.pending, (state) => {
        state.contactPreviewing = true;
        state.error = null;
      })
      .addCase(previewBulkContactCombinations.fulfilled, (state, action) => {
        state.contactPreviewing = false;
        state.previewContactCombinations = action.payload.combinations;
        state.previewContactCount = action.payload.count;
      })
      .addCase(previewBulkContactCombinations.rejected, (state, action) => {
        state.contactPreviewing = false;
        state.error = action.payload;
      })
      .addCase(bulkLinkContactSpecs.pending, (state) => {
        state.contactBulkLinking = true;
        state.error = null;
      })
      .addCase(bulkLinkContactSpecs.fulfilled, (state) => {
        state.contactBulkLinking = false;
        state.previewContactCombinations = [];
        state.previewContactCount = 0;
      })
      .addCase(bulkLinkContactSpecs.rejected, (state, action) => {
        state.contactBulkLinking = false;
        state.error = action.payload;
      })
      .addCase(unlinkContactSpec.fulfilled, (state, action) => {
        state.contactSpecs = state.contactSpecs.filter((s) => s.id !== action.payload);
      })
      .addCase(extractInquiryRowsFromFile.pending, (state) => {
        state.extracting = true;
        state.error = null;
      })
      .addCase(extractInquiryRowsFromFile.fulfilled, (state, action) => {
        state.extracting = false;
        state.extractedRows = action.payload;
      })
      .addCase(extractInquiryRowsFromFile.rejected, (state, action) => {
        state.extracting = false;
        state.error = action.payload;
      })
      .addCase(searchLeads.pending, (state) => {
        state.leadSearching = true;
      })
      .addCase(searchLeads.fulfilled, (state, action) => {
        state.leadSearching = false;
        state.leadOptions = action.payload;
      })
      .addCase(searchLeads.rejected, (state) => {
        state.leadSearching = false;
      })
      .addCase(updateLeadClassification.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(saveInquiryRow.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveInquiryRow.fulfilled, (state, action) => {
        state.saving = false;
        state.lastSavedRow = action.payload;
        state.matchedVendors = action.payload.matchedVendors || [];
      })
      .addCase(saveInquiryRow.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateInquiryRow.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateInquiryRow.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateInquiryRow.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateInquiryRowsBulk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateInquiryRowsBulk.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateInquiryRowsBulk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(saveInquiryRowsBulk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveInquiryRowsBulk.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveInquiryRowsBulk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(fetchInquiryRows.fulfilled, (state, action) => {
        state.inquiryRows = action.payload.rows;
        state.inquiryRowsTotal = action.payload.total;
      })
      .addCase(fetchInquiryRowProblems.fulfilled, (state, action) => {
        state.inquiryRows = action.payload.rows;
      })
      .addCase(deleteInquiryRow.fulfilled, (state, action) => {
        state.inquiryRows = state.inquiryRows.filter((r) => r.id !== action.payload);
      })
      .addCase(notifyMatchedVendors.pending, (state) => {
        state.notifying = true;
        state.error = null;
      })
      .addCase(notifyMatchedVendors.fulfilled, (state, action) => {
        state.notifying = false;
        state.notifyResult = action.payload;
      })
      .addCase(notifyMatchedVendors.rejected, (state, action) => {
        state.notifying = false;
        state.error = action.payload;
      })
      .addCase(submitInquiryRow.pending, (state) => {
        state.notifying = true;
        state.error = null;
      })
      .addCase(submitInquiryRow.fulfilled, (state, action) => {
        state.notifying = false;
        state.notifyResult = action.payload;
      })
      .addCase(submitInquiryRow.rejected, (state, action) => {
        state.notifying = false;
        state.error = action.payload;
      })
      .addCase(submitInquiryRowsBulk.pending, (state) => {
        state.notifying = true;
        state.error = null;
      })
      .addCase(submitInquiryRowsBulk.fulfilled, (state, action) => {
        state.notifying = false;
        const results = action.payload.results || [];
        state.notifyResult = {
          dispatchedVia: "bulk-submit",
          rowCount: results.length,
          // totalVendorsEmailed is the real, deduped count of vendors an
          // email actually went out to for this batch (one per vendor per
          // RFQ, not one per row) — prefer it; fall back to the old summed
          // per-row count only if an older backend build doesn't send it.
          sent: action.payload.totalVendorsEmailed ?? results.reduce((sum, r) => sum + (r.result?.sent || 0), 0),
          rows: results,
          // Delivery reports from the backend (emailed vs matched, and why the rest were not).
          reports: action.payload.reports || [],
        };
      })
      .addCase(submitInquiryRowsBulk.rejected, (state, action) => {
        state.notifying = false;
        state.error = action.payload;
      });
  },
});

export const { clearMatchResults, clearPreview, clearExtractedRows, removeExtractedRow } =
  vendorMaterialSpecsSlice.actions;
export default vendorMaterialSpecsSlice.reducer;
